<script setup>
    import { CashTransactionIcon } from '@/components/icons'
    import { TextInput } from '@/components/shared'
    import { useActiveRoute } from '@/composables/useActiveRoute'
    import NumberFormatter from '@/utils/NumberFormatter.vue'
    import { getIcon } from '@/utils/PaymentTypeIcon'
    import { ArrowPathIcon, MinusIcon, PlusIcon } from '@heroicons/vue/24/outline'
    import { useForm, usePage } from '@inertiajs/vue3'
    import { computed, ref } from 'vue'

    const { isActive } = useActiveRoute()

    const isLoading = ref(false)

    // VAT is now calculated in the backend (CashTransactionService)
    // Adjustments are excluded from VAT calculation there
    const totalCash = computed(() => usePage().props.i_transactions.overview.total_cash)
    const totalCard = computed(() => usePage().props.i_transactions.overview.total_card)
    const totalDepositsHeld = computed(() => usePage().props.i_transactions.overview.total_deposits_held)

    const cashboxForm = useForm({
        amount: '',
        note: '',
        type: 'adjustment',
        payment_type: 'cash',
    })

    const submitCashboxOperation = (operation) => {
        if (!cashboxForm.amount) return

        cashboxForm.amount = operation === 'minus' ? -Math.abs(cashboxForm.amount) : Math.abs(cashboxForm.amount)

        if (cashboxForm.note.trim() === '') {
            cashboxForm.note = operation === 'minus' ? 'Výběr z kasy' : 'Vklad do kasy'
        }

        cashboxForm.post(route('cash-transactions.store'), {
            preserveScroll: true,
            preserveState: true,

            onBefore: () => {
                isLoading.value = true
            },
            onSuccess: () => {
                cashboxForm.reset()
            },
            onError: () => {
                //
            },
            onFinish: () => {
                setTimeout(() => {
                    isLoading.value = false
                }, 500)
            },
        })
    }
</script>

<template>
    <div class="relative -mx-3 mb-5 rounded-lg border border-gray-200 bg-gray-50 p-2">
        <div
            v-show="isLoading"
            class="absolute inset-0 z-10 flex items-center justify-center rounded-lg bg-black/20 backdrop-blur-[3px]"
        >
            <ArrowPathIcon class="size-24 animate-spin text-white" />
        </div>

        <div class="mb-4 space-y-2">
            <div class="flex items-center justify-between">
                <Link
                    :href="route('cash-transactions.index')"
                    :class="[
                        isActive('cash-transactions.*')
                            ? 'border-gray-200 bg-white text-green-600'
                            : 'border-gray-200 bg-gray-100 text-gray-700 hover:bg-gray-50 hover:text-green-600',
                        'group flex w-full gap-x-3 rounded-md border p-2 text-sm/6 font-semibold',
                    ]"
                    prefetch
                    cache-for="4s"
                >
                    <CashTransactionIcon
                        :class="[
                            isActive('cash-transactions.*')
                                ? 'text-green-600'
                                : 'text-gray-400 group-hover:text-green-600',
                            'size-6 shrink-0',
                        ]"
                        aria-hidden="true"
                    />
                    Tržby
                </Link>
            </div>

            <div class="mt-2 flex items-center justify-between">
                <div class="flex items-center">
                    <component
                        :is="getIcon('banknotes-icon')"
                        class="mr-2 size-4 text-green-600"
                    />
                    <span class="text-sm text-gray-600">Hotovost:</span>
                </div>

                <span class="font-semibold text-gray-900">
                    <NumberFormatter
                        :value="totalCash"
                        :min_decimals="2"
                        :max_decimals="2"
                        unit="Kč"
                    />
                </span>
            </div>

            <div class="mt-2 flex items-center justify-between">
                <div class="flex items-center">
                    <component
                        :is="getIcon('credit-card-icon')"
                        class="mr-2 size-4 text-blue-600"
                    />
                    <span class="text-sm text-gray-600">Karta:</span>
                </div>

                <span class="font-semibold text-gray-900">
                    <NumberFormatter
                        :value="totalCard"
                        :min_decimals="2"
                        :max_decimals="2"
                        unit="Kč"
                    />
                </span>
            </div>

            <div class="flex items-center justify-between">
                <div class="flex items-center">
                    <ArrowPathIcon class="mr-2 size-4 text-orange-600" />
                    <span class="text-sm text-gray-600">Kauce:</span>
                </div>

                <span class="font-semibold text-gray-900">
                    <NumberFormatter
                        :value="totalDepositsHeld"
                        :min_decimals="2"
                        :max_decimals="2"
                        unit="Kč"
                    />
                </span>
            </div>
        </div>

        <form
            @submit.prevent
            class="space-y-3"
        >
            <div class="flex">
                <TextInput
                    for="cashbox-amount"
                    v-model="cashboxForm.amount"
                    type="number"
                    step="0.01"
                    min="0"
                    placeholder="0.00"
                    class="w-full"
                    :disabled="isLoading"
                />

                <div class="ml-2 flex">
                    <button
                        type="button"
                        @click="submitCashboxOperation('minus')"
                        class="inline-flex items-center rounded-l-md border border-red-300 bg-red-50 px-3 py-2 text-red-500 hover:bg-red-100 focus:border-green-500 focus:ring-1 focus:ring-green-500 focus:outline-none disabled:cursor-not-allowed disabled:opacity-70"
                        :disabled="!cashboxForm.amount || isLoading"
                    >
                        <MinusIcon class="size-4 text-red-500" />
                    </button>

                    <button
                        type="button"
                        @click="submitCashboxOperation('plus')"
                        class="inline-flex items-center rounded-r-md border border-l-0 border-green-300 bg-green-50 px-3 py-2 text-green-500 hover:bg-green-100 focus:border-green-500 focus:ring-1 focus:ring-green-500 focus:outline-none disabled:cursor-not-allowed disabled:opacity-70"
                        :disabled="!cashboxForm.amount || isLoading"
                    >
                        <PlusIcon class="size-4 text-green-500" />
                    </button>
                </div>
            </div>

            <div>
                <TextInput
                    id="cashbox-note"
                    v-model="cashboxForm.note"
                    type="text"
                    placeholder="Důvod vkladu/výběru"
                    class="block w-full rounded-md border-gray-300 px-3 py-2 text-sm focus:border-green-500 focus:ring-green-500"
                    :disabled="isLoading"
                    required
                />
            </div>

            <div
                v-if="cashboxForm.errors.amount || cashboxForm.errors.note || cashboxForm.errors.type"
                class="text-xs text-red-600"
            >
                <p v-if="cashboxForm.errors.amount">{{ cashboxForm.errors.amount }}</p>
                <p v-if="cashboxForm.errors.note">{{ cashboxForm.errors.note }}</p>
                <p v-if="cashboxForm.errors.type">{{ cashboxForm.errors.type }}</p>
            </div>
        </form>
    </div>
</template>
